// Heartbeat.tsx
import React, { useMemo } from "react";
import { motion } from "framer-motion";

interface HeartbeatProps {
  lineColor?: string;
  speed?: number; // seconds for one full scroller cycle
  strokeWidth?: number;
}

const Heartbeat: React.FC<HeartbeatProps> = ({
  lineColor = "#6691FF",
  speed = 4,
  strokeWidth = 2.2,
}) => {
  // Single tile segments that emulate your screenshot (baseline, small bump, big QRS, baseline, small spike, baseline)
  const segments = useMemo(
    () => [
      // short baseline
      "M0 50 L8 50",

      // stronger QRS (deeper Q, taller R, sharper S)
      "M8 50 L10 70 L12 10 L14 90 L16 50",

      // baseline with small beat
      "M16 50 L26 50 L28 42 L30 58 L32 50 L40 50",

      // second smaller spike (kept from your version)
      "M40 50 L42 36 L44 64 L46 50",

      // flat to end
      "M46 50 L100 50",
    ],
    []
  );

  /*  "M0 50 L10 50",
      "M10 50 L12 46 L14 50",
      "M14 50 L18 50 L20 18 L22 82 L24 50",
      "M24 50 L32 50",
      "M32 50 L34 32 L36 68 L38 50",
      "M38 50 L100 50",

        "M0 50 L8 50",
      "M8 50 L10 60 L12 18 L14 82 L16 50",
      "M16 50 L40 50",
      "M40 50 L42 36 L44 64 L46 50",
      "M46 50 L100 50",
      
      
      */

  const tileCount = 2; // two tiles for seamless scrolling

  const internalStyles = `
    /* Container + scroller */
    .hb-scroller {
      display: flex;
      width: 200%;
      height: 100%;
      will-change: transform;
    }
    .hb-tile {
      flex: 0 0 50%;
      height: 100%;
      min-width: 50%;
    }
    .hb-svg {
      width: 100%;
      height: 100%;
      display: block;
    }

    /* subtle center baseline */
    .hb-svg line.center {
      stroke: rgba(255,255,255,0.02);
      stroke-width: 0.8;
    }

    /* --- DRAW ANIMATION ---
       We use stroke-dasharray with a large constant (1000) so we don't need to compute exact path length.
       Each path will animate stroke-dashoffset from 1000 -> 0 to appear "drawn".
    */
    .main-path {
      stroke-linecap: round;
      stroke-linejoin: round;
      vector-effect: non-scaling-stroke;
      stroke-dasharray: 1000;
      stroke-dashoffset: 1000;
      /* animation is set inline to control duration + delay per-segment */
      animation-name: hb-draw;
      animation-timing-function: linear;
      animation-iteration-count: infinite;
    }

    @keyframes hb-draw {
      0%   { stroke-dashoffset: 1000; opacity: 1; }
      60%  { stroke-dashoffset: 0; opacity: 1; } /* fully drawn */
      100% { stroke-dashoffset: 0; opacity: 0.95; } /* slight fade while repeating */
    }

    /* Slight glow stroke behind */
    .glow-path {
      stroke-linecap: round;
      stroke-linejoin: round;
      vector-effect: non-scaling-stroke;
      opacity: 0.12;
    }

    /* small vertical wiggle per segment (uvijanje) */
    .seg {
      transform-box: fill-box;
      transform-origin: center;
      /* animation assigned inline to vary duration/delay per-segment */
      animation-name: seg-wiggle;
      animation-iteration-count: infinite;
      animation-timing-function: ease-in-out;
    }
    @keyframes seg-wiggle {
      0% { transform: translateY(0px); }
      25% { transform: translateY(-1.4px); }
      50% { transform: translateY(0px); }
      75% { transform: translateY(0.4px); }
      100% { transform: translateY(0px); }
    }

    /* Subtle stroke drop shadow for glow */
    .ecg-stroke {
      filter: drop-shadow(0 0 6px rgba(102,145,255,0.12));
    }
  `;

  return (
    <div
      className="relative w-full h-full overflow-hidden ecg-grid"
      style={{ minHeight: 80 }}
    >
      <style>{internalStyles}</style>

      {/* MASK on the whole component so edges fade out horizontally */}
      <motion.div
        className="hb-scroller"
        animate={{ x: ["0%", "-50%"] }}
        transition={{
          duration: speed,
          repeat: Infinity,
          ease: "linear",
        }}
      >
        {[...Array(tileCount)].map((_, tileIdx) => (
          <div className="hb-tile" key={tileIdx}>
            <svg
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              className="hb-svg"
              role="img"
              aria-label="ECG heartbeat trace"
              // horizontal fade mask so the entire tile fades at left/right edges
              style={{
                WebkitMaskImage:
                  "linear-gradient(to right, transparent, black 12%, black 88%, transparent)",
                WebkitMaskRepeat: "no-repeat",
                WebkitMaskSize: "100% 100%",
                maskImage:
                  "linear-gradient(to right, transparent, black 12%, black 88%, transparent)",
                maskRepeat: "no-repeat",
                maskSize: "100% 100%",
              }}
            >
              {/* center faint baseline */}
              <line className="center" x1="0" y1="50" x2="100" y2="50" />

              {/* main stroke group */}
              <g
                className="ecg-stroke"
                style={{ color: lineColor /* currentColor used below */ }}
              >
                {segments.map((segPath, i) => {
                  // stagger animation delays so the draw flows along the tile
                  const perSegDelay =
                    (i * (speed / Math.max(segments.length, 1))) / 2; // adjust factor for visual pacing
                  const wiggleDuration = 1.6 + (i % 3) * 0.18; // slightly different wiggle durations
                  return (
                    <g
                      className="seg"
                      key={`seg-${tileIdx}-${i}`}
                      // inline animation control for each segment to get variety
                      style={{
                        animationDuration: `${wiggleDuration}s`,
                        animationDelay: `${perSegDelay}s`,
                      }}
                    >
                      <path
                        className="main-path"
                        d={segPath}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={strokeWidth}
                        // inline animation duration + delay to sync drawing with scroller speed
                        style={{
                          animationDuration: `${speed}s`,
                          animationDelay: `${
                            perSegDelay + tileIdx * (speed / 2)
                          }s`,
                        }}
                      />
                    </g>
                  );
                })}
              </g>

              {/* Glow overlay (thicker transparent stroke for vivid effect) */}
              <g style={{ color: lineColor }} className="ecg-stroke">
                {segments.map((segPath, i) => (
                  <path
                    key={`glow-${tileIdx}-${i}`}
                    className="glow-path"
                    d={segPath}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={strokeWidth * 2.2}
                    opacity={0.12}
                  />
                ))}
              </g>
            </svg>
          </div>
        ))}
      </motion.div>

      {/* Cover gradient left/right (extra darkness on container edges) */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/40 rounded-xl" />
      </div>
    </div>
  );
};

export default Heartbeat;
